import type { ServitutReportState } from "../model/report";
import { createSkeletonRow, type ServitutRow } from "../model/row";
import type { ReportPreparedPayload } from "../store/actions";

const CASE_DESCRIPTION_KEY = "servitut-redegoerelse:last-case-description";
const SKELETON_KEY = "servitut-redegoerelse:last-skeleton";

export function loadLastCaseDescription(): string {
   try {
      return localStorage.getItem(CASE_DESCRIPTION_KEY) || "";
   } catch {
      return "";
   }
}

export function saveLastCaseDescription(value: string) {
   try {
      localStorage.setItem(CASE_DESCRIPTION_KEY, value);
   } catch (error) {
      console.warn("[Servitutredegørelse] failed to save case description", error);
   }
}

type SessionSkeleton = {
   caseDescription: string;
   columns: number[];
   columnLabels: Record<string, string>;
   columnDetails: ReportPreparedPayload["columnDetails"];
   uniqueAktCount: number;
   aktSearchByBfe: ReportPreparedPayload["aktSearchByBfe"];
   rows: Array<{
      id: string;
      identity: ServitutRow["identity"];
      landRegistry: ServitutRow["landRegistry"];
      document: Exclude<ServitutRow["document"], { kind: "annotation" }>;
      pdfStatus: ServitutRow["pdfStatus"];
      sourceMessage?: string;
   }>;
   savedAt: string;
};

function skeletonDocument(row: ServitutRow): SessionSkeleton["rows"][number]["document"] {
   if (row.document.kind === "annotation") {
      return row.registryBinding || { kind: "none" };
   }
   if (row.document.kind === "none" || row.document.kind === "rigsarkivet") {
      return row.document;
   }
   return row.document;
}

export function saveReportSkeleton(state: ServitutReportState) {
   if (state.status !== "ready" || !state.rowOrder.length) {
      return;
   }
   const cache: SessionSkeleton = {
      caseDescription: state.caseDescription,
      columns: state.columns,
      columnLabels: state.columnLabels,
      columnDetails: state.columnDetails,
      uniqueAktCount: state.uniqueAktCount,
      aktSearchByBfe: state.aktSearchByBfe,
      rows: state.rowOrder.map((id) => {
         const row = state.rows[id];
         return {
            id: row.id,
            identity: row.identity,
            landRegistry: row.landRegistry,
            document: skeletonDocument(row),
            pdfStatus: row.document.kind === "annotation" ? "ready" : row.pdfStatus,
            sourceMessage: row.sourceMessage,
         };
      }),
      savedAt: new Date().toISOString(),
   };
   try {
      sessionStorage.setItem(SKELETON_KEY, JSON.stringify(cache));
      sessionStorage.removeItem("servitut-redegoerelse:last-report");
   } catch (error) {
      console.warn("[Servitutredegørelse] failed to save session skeleton", error);
   }
}

export function loadReportSkeleton(): ReportPreparedPayload | null {
   try {
      const raw = sessionStorage.getItem(SKELETON_KEY);
      if (!raw) {
         return null;
      }
      const parsed = JSON.parse(raw) as SessionSkeleton;
      if (!Array.isArray(parsed.rows) || !parsed.rows.length) {
         return null;
      }
      return {
         rows: parsed.rows.map((row) => createSkeletonRow({
            id: row.id,
            identity: row.identity,
            landRegistry: row.landRegistry,
            document: row.document,
            pdfStatus: row.pdfStatus,
            sourceMessage: row.sourceMessage,
         })),
         columns: parsed.columns || [],
         columnLabels: parsed.columnLabels || {},
         columnDetails: parsed.columnDetails || {},
         uniqueAktCount: parsed.uniqueAktCount || 0,
         metadataById: {},
         aktSearchByBfe: parsed.aktSearchByBfe || {},
         caseDescription: parsed.caseDescription,
      };
   } catch (error) {
      console.warn("[Servitutredegørelse] failed to restore session skeleton", error);
      return null;
   }
}

export function clearStalePreparedCache() {
   try {
      sessionStorage.removeItem("servitut-redegoerelse:last-report");
   } catch {
      // ignore
   }
}
